import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Label } from '@client/components/ui/label';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { AlertCircle, Send, Users, Mail, CheckCircle, Loader2, Wand2 } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@client/components/ui/radio-group';

type Step = 'email' | 'access' | 'review';

const stepLabels: Record<Step, string> = {
  email: 'Advisor Email',
  access: 'Client Access',
  review: 'Review & Confirm',
};

interface InviteAdvisorDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: (email: string, enabledClientAccess: boolean) => void;
  onDirectAssign?: (email: string, enabledClientAccess: boolean) => void;
  validateEmail: (email: string) => boolean;
  emailError?: string | null;
  onEmailChange?: (email: string) => void;
  isMutating?: boolean;
  currentMutation?: 'invite' | 'direct' | null;
  isAdminMode?: boolean;
}

export function InviteAdvisorDialog({
  open,
  onClose,
  onInvite,
  onDirectAssign,
  validateEmail,
  emailError,
  onEmailChange,
  isMutating = false,
  currentMutation = null,
  isAdminMode = false,
}: InviteAdvisorDialogProps) {
  const [step, setStep] = useState<Step>('email');
  const [emailInput, setEmailInput] = useState('');
  const [clientAccess, setClientAccess] = useState<'yes' | 'no'>('yes');

  useEffect(() => {
    if (open) {
      setStep('email');
      setEmailInput('');
      setClientAccess('yes');
    }
  }, [open]);

  const handleClose = () => {
    if (isMutating) return;
    setStep('email');
    setEmailInput('');
    setClientAccess('yes');
    onClose();
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    onEmailChange?.(value);
  };

  const handleContinue = () => {
    if (emailInput && !emailError && validateEmail(emailInput)) {
      setStep('access');
    }
  };

  const handleSendInvite = () => {
    if (emailInput && !emailError) {
      onInvite(emailInput, clientAccess === 'yes');
    }
  };

  const handleDirectAssign = () => {
    if (emailInput && !emailError && onDirectAssign) {
      onDirectAssign(emailInput, clientAccess === 'yes');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">Invite Advisor</DialogTitle>
          <DialogDescription className="text-gray-700">{stepLabels[step]}</DialogDescription>
        </DialogHeader>

        {/* Step: Email Input */}
        {step === 'email' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Mail className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Enter the advisor's email address
                  </h3>
                  <p className="text-sm text-gray-700">
                    We'll send them an invitation to join your team
                  </p>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="text-primary-dark"
                  >
                    Advisor Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={emailInput}
                    onChange={e => handleEmailInputChange(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleContinue();
                    }}
                  />
                </div>
                {emailError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{emailError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!emailInput || !!emailError}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step: Client Access */}
        {step === 'access' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Users className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Do you need access to this advisor's clients?
                  </h3>
                  <p className="text-sm text-gray-700">
                    This determines view/edit access to this advisor's clients
                  </p>
                </div>
              </div>
              <RadioGroup
                value={clientAccess}
                onValueChange={value => setClientAccess(value as 'yes' | 'no')}
                className="gap-2"
              >
                <div className="flex items-center space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <RadioGroupItem
                    value="yes"
                    id="client-access-yes"
                  />
                  <Label
                    htmlFor="client-access-yes"
                    className="flex-1 cursor-pointer text-base font-normal text-gray-900"
                  >
                    Yes, I need to view and edit their clients
                  </Label>
                </div>
                <div className="flex items-center space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <RadioGroupItem
                    value="no"
                    id="client-access-no"
                  />
                  <Label
                    htmlFor="client-access-no"
                    className="flex-1 cursor-pointer text-base font-normal text-gray-900"
                  >
                    No, I don't need client access
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep('email')}
              >
                Back
              </Button>
              <Button onClick={() => setStep('review')}>Continue</Button>
            </DialogFooter>
          </>
        )}

        {/* Step: Final Review */}
        {step === 'review' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <CheckCircle className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">Review and Confirm</h3>
                  <p className="text-sm text-gray-700">
                    Please verify the details before proceeding
                  </p>
                </div>
              </div>

              <div className="space-y-4 rounded-md border p-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Advisor Email</p>
                  <p className="text-base text-gray-900">{emailInput}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Client Access</p>
                  <p className="text-base text-gray-900">
                    {clientAccess === 'yes'
                      ? 'Yes, you will have access to their clients'
                      : 'No, you will not have access to their clients'}
                  </p>
                </div>
              </div>

              <div className="bg-primary-50 border-primary-100 mt-2 rounded-md border p-4">
                <p className="text-primary-dark text-sm">
                  Note: You will have access to view the advisor's subscription and usage statistics
                  by default.
                </p>
              </div>
            </div>

            <DialogFooter>
              <div className="flex w-full justify-between">
                {isAdminMode && onDirectAssign ? (
                  <Button
                    onClick={handleDirectAssign}
                    disabled={isMutating}
                    variant="outline"
                  >
                    {isMutating && currentMutation === 'direct' ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <Wand2 className="mr-1 h-4 w-4" />
                        Assign Directly
                      </>
                    )}
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('access')}
                    disabled={isMutating}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSendInvite}
                    disabled={isMutating}
                  >
                    {isMutating && currentMutation === 'invite' ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-1 h-4 w-4" />
                        Send Invite
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
